import { DeleteOutlined } from "@ant-design/icons";
import { useNotification } from "@refinedev/core";
import { Button, Col, Form, Input, Modal, Row, Space, Table, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useCreateWallet,
  useDeleteWallet,
  useUpdateWallet,
} from "../../../../../api/wallet";
import { useWalletManagement } from "../../../../../utils/hooks/useWalletManagement";
import { useIsAdminRole } from "../../../../../utils/hooks/useIdentityAdmin.hook";
import { ButtonListHeader } from "../../../../components/buttonsList";
import { Row as RowTable } from "../../../../components/interface";
import FormWallet from "./form-wallet";

interface IDetailWalletProps {
  tabActive: "info" | "bank" | "wallet";
  id: string | undefined;
  userId: number | undefined;
  isActive?: boolean;
}

const start = 0;
const end = 20;

const DetailWallet = ({ tabActive, id, userId, isActive = true }: IDetailWalletProps) => {
  const [searchForm] = Form.useForm();
  const [walletForm] = Form.useForm();
  const [actionWallet, setActionWallet] = useState<"ADD" | "EDIT" | null>(null);
  const [walletId, setWalletId] = useState<number | null>(null);
  const [deleteWalletId, setDeleteWalletId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({ start, end });
  const [searchName, setSearchName] = useState<string>("");
  const { open: openNotification } = useNotification();
  const isAdminRole = useIsAdminRole();

  const { dataListWallet, refetch, isLoading } = useWalletManagement(
    Number(id || 0),
    params,
    searchName,
    isActive
  );

  const moveToAvailablePageAfterDelete = useCallback(async () => {
    const result = await refetch();
    const responseData: any = result.data?.data?.data;
    const total = Number(responseData?.total ?? 0);
    const fallbackPageSize = dataListWallet.pageSize || end - start;
    const pageSize = Number(responseData?.pageSize ?? fallbackPageSize);
    const maxPage = Math.max(1, Math.ceil(total / pageSize));

    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
      setParams({
        start: (maxPage - 1) * pageSize,
        end: maxPage * pageSize,
      });
    }
  }, [currentPage, dataListWallet.pageSize, refetch]);

  const { mutate: createWallet, isLoading: isLoadingCreate } = useCreateWallet({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification?.({
          key: "add_wallet_success",
          message: response.data.message || "Wallet address created successfully!",
          type: "success",
        });
      }
      refetch();
      setActionWallet(null);
      setWalletId(null);
      walletForm.resetFields();
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "add_wallet_failure",
          message: error?.message || "Failed to create wallet address!",
          type: "error",
        });
      }
    },
  });

  const { mutate: updateWallet, isLoading: isLoadingUpdate } = useUpdateWallet({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification?.({
          key: "update_wallet_success",
          message: response.data.message || "Wallet address updated successfully!",
          type: "success",
        });
      }
      refetch();
      setActionWallet(null);
      setWalletId(null);
      walletForm.resetFields();
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "update_wallet_failure",
          message: error?.message || "Failed to update wallet address!",
          type: "error",
        });
      }
    },
  });

  const { mutate: deleteWallet, isLoading: loadingDeleteWallet } =
    useDeleteWallet({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "delete_wallet_success",
            message: response.data.message || "Delete wallet successfully!",
            type: "success",
          });
        }
        void moveToAvailablePageAfterDelete();
        setDeleteWalletId(null);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "delete_wallet_failure",
            message: error?.message || "Delete wallet failed!",
            type: "error",
          });
        }
      },
    });

  const handleSearch = (values: { searchValue?: string }) => {
    setSearchName(values.searchValue || '');
    setCurrentPage(1);
    setParams({ start, end });
  };

  useEffect(() => {
    searchForm.setFieldsValue({ searchValue: searchName });
  }, [searchName, searchForm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setParams({
      start: (page - 1) * dataListWallet.pageSize || start,
      end: page * dataListWallet.pageSize || end,
    });
  };

  const handleCancel = () => {
    if (isLoadingCreate || isLoadingUpdate) return;
    setActionWallet(null);
    setWalletId(null);
    walletForm.resetFields();
  };

  const onSubmitWallet = (values: any) => {
    const body = {
      companyId: Number(id),
      userId: Number(userId),
      name: values.name || "",
      assetConfigGroupId: Number(values.assetConfigGroupId) || null,
      networkId: Number(values.networkId) || null,
      address: values.address || "",
    };
    if (actionWallet === "ADD") {
      createWallet(body as any);
    } else {
      if (!walletId) return;
      updateWallet({
        id: walletId,
        ...body,
      } as any);
    }
  };

  const handleOpenEditWallet = useCallback(
    (id: number) => {
      walletForm.resetFields();
      setActionWallet("EDIT");
      setWalletId(id);
    },
    [walletForm]
  );

  const handleCancelDelete = () => {
    if (loadingDeleteWallet) return;
    setDeleteWalletId(null);
  };

  const handleSubmitDelete = () => {
    if (deleteWalletId) {
      deleteWallet(deleteWalletId);
    }
  };

  const columns = useMemo((): ColumnType<RowTable>[] => {
    const columns: ColumnType<RowTable>[] = [
      {
        dataIndex: "id",
        title: <span className={"column-header"}>NO.</span>,
        render: (_value, _record, index) => {
          return (
            <Typography className="text-table">
              {(dataListWallet.page - 1) * dataListWallet.pageSize + index + 1}
            </Typography>
          );
        },
        onCell: (record: RowTable) => ({
          onClick: () => {
            handleOpenEditWallet(record.id as number);
          },
        }),
        width: "6%",
      },
      {
        dataIndex: "name",
        title: <span className={"column-header"}>NAME</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            handleOpenEditWallet(record.id as number);
          },
        }),
        render: (value) => {
          return <Typography className="text-table">{value}</Typography>;
        },
        width: "18.7%",
      },
      {
        dataIndex: "assetConfigGroupId",
        title: <span className={"column-header"}>CURRENCY</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            handleOpenEditWallet(record.id as number);
          },
        }),
        render: (value, data: any) => {
          return (
            <Typography className="text-table">
              {data.assetConfigGroup?.symbol}
            </Typography>
          );
        },
        width: "13.5%",
      },
      {
        dataIndex: "networkId",
        title: <span className={"column-header"}>NETWORK</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            handleOpenEditWallet(record.id as number);
          },
        }),
        render: (value, data: any) => {
          return (
            <Typography className="text-table ">{data.network?.name}</Typography>
          );
        },
        width: "19.5%",
      },
      {
        dataIndex: "address",
        title: <span className={"column-header"}>ADDRESS</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            handleOpenEditWallet(record.id as number);
          },
        }),
        render: (value) => {
          return <Typography className="text-table">{value}</Typography>;
        },
        width: "30%",
      },
      {
        dataIndex: "actions",
        title: "",
        render: (_value, record: RowTable) =>
          isAdminRole && (
            <Button
              className="list-button list-button--delete"
              icon={<DeleteOutlined style={{ fontSize: "19px" }} />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteWalletId(record.id as number);
              }}
            />
          ),
        width: "8%",
      },
    ];
    return columns.filter(({ key }) => !key);
  }, [dataListWallet, handleOpenEditWallet, isAdminRole]);

  return (
    <div
      style={{ display: tabActive === "wallet" ? "block" : "none" }}
    >
      <Form
        form={searchForm}
        layout="vertical"
        onFinish={handleSearch}
        style={{
          background: "#FAFAFA",
          borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          padding: "16px 10px 0",
        }}
      >
        <Row justify="space-between" align="middle">
          <Col lg={8} md={10}>
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item
                name="searchValue"
                style={{ width: "100%" }}
                initialValue={searchName}
              >
                <Input
                  placeholder="Search by name"
                  allowClear
                  onChange={(e) => !e.currentTarget.value && searchForm.submit()}
                />
              </Form.Item>
              <Button
                type="primary"
                style={{ borderRadius: "0px 6px 6px 0px", marginTop: "5px" }}
                onClick={() => searchForm.submit()}
              >
                Search
              </Button>
            </Space.Compact>
          </Col>
          {isAdminRole && (
            <Col>
              {ButtonListHeader(() => {
                setActionWallet("ADD");
                setWalletId(null);
                walletForm.resetFields();
              })}
            </Col>
          )}
        </Row>
      </Form>

      <Table
        rowKey="id"
        columns={columns as any}
        dataSource={dataListWallet.items}
        loading={isLoading}
        pagination={{
          current: currentPage,
          total: dataListWallet.total,
          pageSize: dataListWallet.pageSize,
          onChange: handlePageChange,
        }}
      />
      <Modal
        open={!!actionWallet}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <div style={{ marginBottom: "0px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {actionWallet === "ADD" ? "Add Wallet Address" : "Edit Wallet Address"}
            </div>
            {actionWallet === "EDIT" && walletId && (
              <div style={{ marginBottom: "0px", display: "flex", alignItems: "center", justifyContent: "center" }}>#{walletId}</div>
            )}
          </>
        }
        className="edit-wallet-modal"
        width={"600px"}
      >
        <FormWallet
          key={`${actionWallet || "CLOSE"}-${walletId || "new"}`}
          form={walletForm}
          walletId={walletId}
          handleCancel={handleCancel}
          onSubmitWallet={onSubmitWallet}
          isLoading={isLoadingCreate || isLoadingUpdate}
          actionWallet={actionWallet}
        />
      </Modal>
      <Modal
        open={!!deleteWalletId}
        title="Confirm Delete"
        className='smartModalSM'
        onOk={handleSubmitDelete}
        onCancel={handleCancelDelete}
        confirmLoading={loadingDeleteWallet}
      >
        <p>Do you want to delete this record?</p>
      </Modal>
    </div>
  );
};

export default DetailWallet;
